import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { Eye, EyeOff, LogIn, Lock, User, ShieldCheck } from "lucide-react";
import { loginUser } from "../redux/userSlice";

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.user);

  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    try {
      const result = await dispatch(loginUser({ email, senha })).unwrap();
      navigate(result.user.nivel === "Caixa" ? "/faturacao/emitir-documento/" : "/dashboard");
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Falha na Autenticação",
        text: err.message || "E-mail de acesso ou senha incorretos.",
        confirmButtonColor: "#0f8b8d",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.nivel === "Caixa") {
      navigate("/faturacao/emitir-documento/");
    } else if (user?.nivel === "Administrador") {
      navigate("/dashboard");
    }
  }, [user, navigate]);

  return (
    <div className="login-shell">
      <div className="login-visual">
        <div className="brand-orb">F</div>
        <h1>Facturize</h1>
        <p>Sistema de Faturação e Gestão Comercial</p>
        <div className="login-metrics">
          <span>AGT Ready</span>
          <span>SAF-T</span>
          <span>Cloud POS</span>
        </div>
      </div>

      <main className="login-panel">
        <div className="login-card">
          <div className="login-card-head">
            <div>
              <span className="eyebrow">Facturize S.A.</span>
              <h2>Entrar no Sistema</h2>
              <p>Introduza as suas credenciais para aceder</p>
            </div>
            <ShieldCheck size={22} />
          </div>

          <form onSubmit={handleSubmit}>
            <label className="field-label">Utilizador / E-mail</label>
            <div className="field-shell">
              <User size={18} />
              <input
                type="text"
                placeholder="exemplo@empresa.com"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </div>

            <div className="d-flex justify-content-between align-items-center mt-3">
              <label className="field-label mb-0">Senha de Acesso</label>
              <Link to="/auth/recuperar-senha" className="mini-link">Esqueceu a senha?</Link>
            </div>
            <div className="field-shell">
              <Lock size={18} />
              <input
                type={showPassword ? "text" : "password"}
                placeholder="********"
                required
                value={senha}
                onChange={(event) => setSenha(event.target.value)}
              />
              <button type="button" className="ghost-eye" onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            <button type="submit" className="login-submit" disabled={loading}>
              {loading ? <span className="spinner-border spinner-border-sm" /> : <><LogIn size={18} /> Aceder à Conta</>}
            </button>
          </form>

          <div className="login-card-foot">
            <span>Não tem uma conta configurada?</span>
            <Link to="/auth/cadastro">Configurar Empresa</Link>
          </div>
        </div>
      </main>

      <style>{`
        .login-shell {
          min-height: 100vh;
          display: grid;
          grid-template-columns: minmax(360px, 0.95fr) minmax(360px, 1.05fr);
          background: var(--fz-bg);
          color: var(--fz-text);
        }

        .login-visual {
          display: flex;
          flex-direction: column;
          justify-content: center;
          padding: 56px;
          background:
            linear-gradient(145deg, rgba(15, 139, 141, 0.88), rgba(16, 32, 39, 0.96)),
            url("https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=1400&q=80");
          background-size: cover;
          background-position: center;
          color: #fff;
        }

        .brand-orb {
          width: 72px;
          height: 72px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 22px;
          background: #fff;
          color: #0f8b8d;
          font-size: 34px;
          font-weight: 900;
          box-shadow: 0 20px 50px rgba(0, 0, 0, 0.24);
        }

        .login-visual h1 {
          margin: 28px 0 8px;
          font-size: clamp(42px, 6vw, 76px);
          font-weight: 900;
          letter-spacing: 0;
        }

        .login-visual p {
          max-width: 460px;
          color: rgba(255, 255, 255, 0.78);
          font-size: 18px;
        }

        .login-metrics {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          margin-top: 30px;
        }

        .login-metrics span {
          padding: 10px 14px;
          border: 1px solid rgba(255, 255, 255, 0.28);
          border-radius: 999px;
          background: rgba(255, 255, 255, 0.1);
          font-size: 12px;
          font-weight: 800;
        }

        .login-panel {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 32px;
        }

        .login-card {
          width: min(100%, 460px);
          padding: 34px;
          border: 1px solid var(--fz-border);
          border-radius: 24px;
          background: var(--fz-surface);
          box-shadow: var(--fz-shadow);
        }

        .login-card-head {
          display: flex;
          justify-content: space-between;
          gap: 20px;
          margin-bottom: 28px;
        }

        .eyebrow,
        .field-label {
          color: var(--fz-muted);
          font-size: 11px;
          font-weight: 900;
          letter-spacing: 0.08em;
          text-transform: uppercase;
        }

        .login-card h2 {
          margin: 6px 0;
          font-size: 30px;
          font-weight: 900;
        }

        .login-card p {
          margin: 0;
          color: var(--fz-muted);
        }

        .field-shell {
          display: flex;
          align-items: center;
          gap: 10px;
          min-height: 52px;
          margin-top: 7px;
          padding: 0 14px;
          border: 1px solid var(--fz-border);
          border-radius: 16px;
          color: var(--fz-primary);
          background: var(--fz-surface-2);
        }

        .field-shell input {
          width: 100%;
          border: 0 !important;
          outline: 0;
          background: transparent;
        }

        .ghost-eye {
          border: 0;
          color: var(--fz-muted);
          background: transparent;
        }

        .mini-link {
          color: var(--fz-primary);
          font-size: 12px;
          font-weight: 800;
          text-decoration: none;
        }

        .login-submit {
          width: 100%;
          min-height: 52px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          margin-top: 24px;
          border: 0;
          border-radius: 16px;
          color: #fff;
          background: var(--fz-primary);
          font-weight: 900;
          box-shadow: 0 15px 30px rgba(15, 139, 141, 0.26);
        }

        .login-submit:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .login-card-foot {
          display: flex;
          justify-content: space-between;
          gap: 12px;
          margin-top: 24px;
          padding-top: 18px;
          border-top: 1px solid var(--fz-border);
          color: var(--fz-muted);
          font-size: 13px;
        }

        .login-card-foot a {
          font-weight: 900;
          text-decoration: none;
        }

        @media (max-width: 900px) {
          .login-shell {
            grid-template-columns: 1fr;
          }

          .login-visual {
            min-height: 300px;
            padding: 34px 24px;
          }

          .login-panel {
            padding: 20px;
          }
        }

        @media (max-width: 520px) {
          .login-card {
            padding: 24px;
            border-radius: 18px;
          }

          .login-card-foot {
            flex-direction: column;
            gap: 6px;
          }
        }
      `}</style>
    </div>
  );
};

export default Login;